import { createContext, useContext } from 'react';
import type { FriendlyError } from '@/lib/errors';
import type { TestQuestion } from '@/lib/api/test';

export type TestStatus =
  | 'idle'
  | 'loading'
  | 'in_progress'
  | 'submitting'
  | 'completed'
  | 'error';

export type InputMode = 'voice' | 'text';

export interface Answer {
  userAnswer: string;
  skipped: boolean;
}

export interface TestResults {
  totalQuestions: number;
  correctAnswers: number;
  scorePercentage: number;
  passed: boolean;
  timeTakenMinutes?: number;
  weakCategories?: Array<{
    category: string;
    total: number;
    correct: number;
    percentage: number;
  }>;
}

export interface ReviewItem {
  questionId: number;
  questionText: string;
  category: string;
  userAnswer: string;
  isCorrect: boolean;
  correctAnswer: string;
  explanation: string | null;
}

export interface TestSessionState {
  testAttemptId: string | null;
  status: TestStatus;
  questions: TestQuestion[];
  totalQuestions: number;

  currentIndex: number;
  answers: Map<number, Answer>;
  inputMode: InputMode;

  results: TestResults | null;
  reviewItems: ReviewItem[];

  error: FriendlyError | null;
}

export interface TestSessionContextType extends TestSessionState {
  currentQuestion: TestQuestion | null;
  currentAnswer: Answer | null;
  answeredCount: number;
  isLastQuestion: boolean;
  isFirstQuestion: boolean;

  startTest: () => Promise<boolean>;
  submitAnswer: (answer: string) => void;
  skipQuestion: () => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  goToQuestion: (index: number) => void;
  setInputMode: (mode: InputMode) => void;
  completeTest: () => Promise<boolean>;
  resetTest: () => void;
  clearError: () => void;
  retryLastAction: () => Promise<void>;
}

export const TestSessionContext = createContext<
  TestSessionContextType | undefined
>(undefined);

export function useTestSession() {
  const context = useContext(TestSessionContext);
  if (context === undefined) {
    throw new Error(
      'useTestSession must be used within a TestSessionProvider'
    );
  }
  return context;
}
